import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class ECCECoffeeGuide extends Component {
  #request;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    for (const element of this.querySelectorAll('[data-enhanced]')) element.hidden = false;
    if (this.dataset.mode === 'bundle') this.updateBundle();
  }

  updatedCallback() {
    super.updatedCallback();
    for (const element of this.querySelectorAll('[data-enhanced]')) element.hidden = false;
    if (!this.#busy && this.dataset.mode === 'bundle') this.updateBundle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
  }

  #status(message) {
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = message;
  }

  #cards() {
    return [...this.querySelectorAll('[data-coffee]')];
  }

  /** @param {SubmitEvent} event */
  recommend(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    if (!form) return;
    const choice = new FormData(form);
    const taste = choice.get('taste');
    const chocolate = 'Ciocolată, caramel și nuci';
    const fruity = 'Fructat și floral';
    const desiredTaste = taste === 'chocolate' ? chocolate : taste === 'fruity' ? fruity : null;
    const matches = this.#cards().filter(card => {
      const methods = (card.dataset.methods || '').split('|');
      const tastes = JSON.parse(card.dataset.tastes || '[]') || [];
      const decaf = card.dataset.decaf === 'true';
      return card.dataset.stock === 'true' && methods.includes(choice.get('method')) &&
        (taste === 'decaf' ? decaf : !decaf) && (!desiredTaste || tastes.includes(desiredTaste));
    }).map((card, index) => {
      const tastes = JSON.parse(card.dataset.tastes || '[]') || [];
      const milkMatch = choice.get('milk') === 'yes' && tastes.includes(chocolate);
      return { card, score: milkMatch ? 1 : 0, index, milkMatch };
    }).sort((a, b) => b.score - a.score || a.index - b.index).slice(0, 3);
    const selected = new Set(matches.map(match => match.card));
    for (const card of this.#cards()) {
      card.hidden = !selected.has(card);
      const reason = card.querySelector('[data-reason]');
      const match = matches.find(candidate => candidate.card === card);
      if (reason && match) {
        reason.textContent = [this.dataset.matched, desiredTaste ? this.dataset.tasteMatch : '', taste === 'decaf' ? this.dataset.decafMatch : '', match.milkMatch ? this.dataset.milkMatch : ''].filter(Boolean).join(' ');
        reason.hidden = false;
      }
    }
    this.#status(matches.length ? `${this.dataset.resultsCount} ${matches.length}` : this.dataset.noMatches);
  }

  reset() {
    this.querySelector('form')?.reset();
    for (const checkbox of this.querySelectorAll('[data-coffee] input[type=checkbox]')) checkbox.checked = false;
    for (const card of this.#cards()) {
      card.hidden = false;
      const reason = card.querySelector('[data-reason]');
      if (reason) reason.hidden = true;
    }
    this.#status('');
  }

  /** @param {Event} event */
  compare(event) {
    const selected = this.querySelectorAll('[data-coffee] input:checked');
    if (selected.length > 3 && event.target instanceof HTMLInputElement) {
      event.target.checked = false;
      this.#status(this.dataset.compareLimit);
      return;
    }
    this.#status(`${this.dataset.compareCount} ${selected.length} / 3`);
  }

  filterComparisons() {
    const selected = [...this.querySelectorAll('[data-coffee] input:checked')];
    if (selected.length < 2) {
      this.#status(`${this.dataset.compareCount} ${selected.length} / 3`);
      return;
    }
    const ids = new Set(selected.map(input => input.value));
    for (const card of this.#cards()) card.hidden = !ids.has(card.dataset.productId);
  }

  #selections() {
    return [...this.querySelectorAll('[data-bag]')].map(select => select.selectedOptions[0]);
  }

  #items() {
    const items = new Map();
    for (const option of this.#selections()) {
      if (!option?.value) continue;
      const current = items.get(option.value);
      if (current) current.quantity++;
      else items.set(option.value, { id: Number(option.value), quantity: 1, properties: { Pachet: this.dataset.property } });
    }
    return [...items.values()];
  }

  updateBundle() {
    const selections = this.#selections();
    const complete = selections.length === 4 && selections.every(option => option?.value && !option.disabled);
    const total = selections.reduce((sum, option) => sum + Number(option?.dataset.price || 0), 0);
    const output = this.querySelector('[data-total]');
    if (output) output.textContent = complete ? new Intl.NumberFormat(this.dataset.locale || 'ro', { style: 'currency', currency: this.dataset.currency || 'RON' }).format(total / 100) : '—';
    const button = this.querySelector('[data-submit]');
    if (button) button.disabled = !complete || this.#busy;
    if (!this.#busy) this.#status(complete ? '' : this.dataset.chooseFour);
  }

  #endpoint(path) {
    return `${this.dataset.root || '/'}${path}`;
  }

  /** @param {SubmitEvent} event */
  async addBundle(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    if (this.#busy || !form?.reportValidity()) return;
    const selections = this.#selections();
    if (selections.length !== 4 || selections.some(option => !option?.value || option.disabled)) return;
    this.#busy = true;
    this.#request = new AbortController();
    this.updateBundle();
    this.#status(this.dataset.adding);
    const items = this.#items();
    try {
      const beforeResponse = await fetch(this.#endpoint('cart.js'), { signal: this.#request.signal });
      if (!beforeResponse.ok) throw new Error();
      const before = await beforeResponse.json();
      for (const item of items) {
        const option = selections.find(candidate => Number(candidate.value) === item.id);
        const limit = Number(option.dataset.limit);
        const existing = before.items.filter(line => line.variant_id === item.id).reduce((sum, line) => sum + line.quantity, 0);
        if (limit >= 0 && existing + item.quantity > limit) {
          this.#status(`${this.dataset.stockError} ${option.dataset.title}.`);
          return;
        }
      }
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(element => element.dataset.sectionId).filter(Boolean))];
      const response = await fetch(this.#endpoint('cart/add.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }), signal: this.#request.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, this.dataset.addError));
        this.dispatchEvent(new CartAddEvent({}, this.id, { didError: true, source: 'product-form-component', sections: result.sections }));
        this.#status(this.dataset.addError);
        return;
      }
      this.dispatchEvent(new CartAddEvent({}, this.id, { source: 'product-form-component', itemCount: 4, sections: result.sections }));
      this.#status(this.dataset.added);
    } catch (error) {
      if (error.name !== 'AbortError') this.#status(this.dataset.addError);
    } finally {
      this.#busy = false;
      const button = this.querySelector('[data-submit]');
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get('ecce-coffee-guide-component')) customElements.define('ecce-coffee-guide-component', ECCECoffeeGuide);
